if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        // The bar is rendered deep inside the product section, whose ancestors
        // (animation/transform/contain wrappers) trap `position: fixed` and
        // constrain width. Relocate to <body> so fixed positioning resolves
        // against the viewport. Moving re-triggers connectedCallback, which then
        // runs setup with the element parented to <body>.
        if (this.parentNode !== document.body) {
          document.body.appendChild(this);
          return;
        }

        this.submitButton = document.getElementById(this.dataset.submitButton);
        this.quantityInput = document.getElementById(this.dataset.quantityInput);
        this.priceSource = document.getElementById(this.dataset.price);

        this.stickyButton = this.querySelector('.sticky-atc__button');
        this.stickyButtonLabel = this.querySelector('.sticky-atc__button-label');
        this.stickyQtyInput = this.querySelector('.sticky-atc__qty-input');
        this.stickyPrice = this.querySelector('.sticky-atc__price');
        this.minusButton = this.querySelector('[name="sticky-minus"]');
        this.plusButton = this.querySelector('[name="sticky-plus"]');

        if (!this.submitButton) return;

        this.setupScrollTrigger();
        this.setupQuantity();
        this.setupAddToCart();
        this.setupStateSync();
      }

      setupScrollTrigger() {
        // We deliberately do NOT use IntersectionObserver here. IO delivers its
        // callbacks asynchronously/throttled, which reads as a "slight lag"
        // before the bar slides up (most noticeable during iOS momentum scroll).
        // It also can't work on tablet/desktop: the product-info column is
        // `position: sticky` (enable_sticky_info) at >=750px, so the main
        // Add-to-Cart button never leaves the viewport and IO never fires —
        // which is why it didn't appear on iPad.
        //
        // Instead we capture the button's natural document offset once and
        // compare it against the scroll position every animation frame, so the
        // bar tracks the finger immediately and triggers on scroll distance
        // regardless of any sticky ancestor.
        this.isVisible = false;
        this.ticking = false;
        this.measure();

        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.updateVisibility();
            this.ticking = false;
          });
        };
        this.onResize = () => {
          this.measure();
          this.updateVisibility();
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        // Media above the button may finish loading after first paint and shift
        // its offset; re-measure once everything has loaded.
        window.addEventListener('load', this.onResize, { once: true });
        this.updateVisibility();
      }

      measure() {
        // Natural (normal-flow) document top of the main Add-to-Cart button,
        // summed through the offsetParent chain. This ignores any sticky paint
        // offset on its container, so the value is stable whether or not the
        // info column is currently pinned. Trigger once the button's *bottom*
        // edge would have scrolled above the top of the viewport.
        let top = 0;
        let node = this.submitButton;
        while (node) {
          top += node.offsetTop || 0;
          node = node.offsetParent;
        }
        this.triggerOffset = top + this.submitButton.offsetHeight;
      }

      updateVisibility() {
        const scrolled = window.pageYOffset || document.documentElement.scrollTop || 0;
        const visible = scrolled > this.triggerOffset;
        if (visible === this.isVisible) return;
        this.isVisible = visible;
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      }

      setupQuantity() {
        if (!this.quantityInput || !this.stickyQtyInput) return;
        this.stickyQtyInput.value = this.quantityInput.value;

        this.plusButton?.addEventListener('click', () => this.stepQuantity(1));
        this.minusButton?.addEventListener('click', () => this.stepQuantity(-1));
        this.stickyQtyInput.addEventListener('change', () =>
          this.syncToMain(this.stickyQtyInput.value)
        );
        this.quantityInput.addEventListener('change', () => {
          this.stickyQtyInput.value = this.quantityInput.value;
        });

        if (typeof subscribe === 'function') {
          try {
            this.quantityUpdateUnsubscriber = subscribe(
              PUB_SUB_EVENTS.quantityUpdate,
              () => {
                this.stickyQtyInput.value = this.quantityInput.value;
              }
            );
          } catch (error) {
            // Defensive: don't let a missing/misbehaving pub/sub global break setup.
          }
        }
      }

      stepQuantity(delta) {
        const step = parseInt(this.quantityInput.step, 10) || 1;
        const current =
          parseInt(this.stickyQtyInput.value, 10) ||
          parseInt(this.quantityInput.value, 10) ||
          1;
        let next = current + delta * step;
        const min = parseInt(this.quantityInput.min, 10);
        const max = this.quantityInput.max
          ? parseInt(this.quantityInput.max, 10)
          : null;
        if (!isNaN(min) && next < min) next = min;
        if (max !== null && next > max) next = max;
        this.syncToMain(next);
      }

      syncToMain(value) {
        const v = String(value);
        if (this.stickyQtyInput) this.stickyQtyInput.value = v;
        this.quantityInput.value = v;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setupAddToCart() {
        this.stickyButton?.addEventListener('click', () => {
          if (this.submitButton.hasAttribute('disabled')) return;
          if (this.quantityInput && this.stickyQtyInput) {
            this.syncToMain(this.stickyQtyInput.value);
          }
          this.submitButton.click();
        });
      }

      setupStateSync() {
        this.reflectState();
        this.stateObserver = new MutationObserver(() => this.reflectState());
        this.stateObserver.observe(this.submitButton, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
        });

        if (this.priceSource && this.stickyPrice) {
          this.reflectPrice();
          this.priceObserver = new MutationObserver(() => this.reflectPrice());
          this.priceObserver.observe(this.priceSource, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      reflectState() {
        const disabled = this.submitButton.hasAttribute('disabled');
        this.stickyButton?.toggleAttribute('disabled', disabled);
        const label = this.submitButton.querySelector('span')?.textContent.trim();
        if (label && this.stickyButtonLabel) this.stickyButtonLabel.textContent = label;
      }

      reflectPrice() {
        this.stickyPrice.innerHTML = this.priceSource.innerHTML;
      }

      disconnectedCallback() {
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        this.stateObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.quantityUpdateUnsubscriber?.();
      }
    }
  );
}
